/**
 * Feed filter configuration for Home Mode activity stream
 *
 * Builds the category hierarchy from EVENT_CATEGORY_MAP and ACTIVITY_EVENT_TYPE_CONFIGS,
 * and provides helpers for computing enabled types, persistence, and state management.
 */

import {
  TimelineEventType,
  TimelineEventCategory,
  EVENT_CATEGORY_MAP,
  ALL_EVENT_TYPES,
  FeedFilterCategory,
  FeedFilterEventType,
  FeedFilterState,
  DEFAULT_FILTER_STATE,
} from 'types/timeline';
import { getActivityEventTypeConfig } from './activityEventTypes';

const FILTER_STORAGE_PREFIX = 'feed-filters:';
const CURRENT_FILTER_VERSION = 1;

/** Event types that support inbound/outbound subtypes */
const PERSPECTIVE_EVENT_TYPES = new Set<TimelineEventType>([
  'perspective_assigned',
  'perspective_unassigned',
]);

/**
 * Build FeedFilterEventType entries for a given category,
 * adding inbound/outbound subtypes for perspective events.
 */
function buildEventTypesForCategory(
  category: TimelineEventCategory
): FeedFilterEventType[] {
  return ALL_EVENT_TYPES
    .filter((et) => EVENT_CATEGORY_MAP[et] === category)
    .map((et) => {
      const config = getActivityEventTypeConfig(et);
      const entry: FeedFilterEventType = {
        value: et,
        labelKey: `feed.filters.eventTypes.${et}`,
        icon: config.icon,
        enabled: true,
      };
      if (PERSPECTIVE_EVENT_TYPES.has(et)) {
        entry.subtypes = [
          {
            id: 'inbound',
            labelKey: 'feed.filters.subtypes.inbound',
            enabled: true,
          },
          {
            id: 'outbound',
            labelKey: 'feed.filters.subtypes.outbound',
            enabled: true,
          },
        ];
      }
      return entry;
    });
}

/**
 * Static category hierarchy for the feed filter bar.
 * 4 categories matching EVENT_CATEGORY_MAP (excludes unused 'admin').
 */
export const FEED_FILTER_CATEGORIES: FeedFilterCategory[] = [
  {
    id: 'userAction',
    labelKey: 'feed.filters.categories.userAction',
    icon: 'pi-user',
    enabled: true,
    eventTypes: buildEventTypesForCategory('userAction'),
  },
  {
    id: 'workflow',
    labelKey: 'feed.filters.categories.workflow',
    icon: 'pi-sitemap',
    enabled: true,
    eventTypes: buildEventTypesForCategory('workflow'),
  },
  {
    id: 'system',
    labelKey: 'feed.filters.categories.system',
    icon: 'pi-cog',
    enabled: true,
    eventTypes: buildEventTypesForCategory('system'),
  },
  {
    id: 'security',
    labelKey: 'feed.filters.categories.security',
    icon: 'pi-shield',
    enabled: true,
    eventTypes: buildEventTypesForCategory('security'),
  },
  {
    // Added with the ledger vocabulary (OQ-3): `aspect_attached` / `aspect_detached` are the first
    // event types to carry the `admin` category. Without this section they appear in the feed but
    // cannot be filtered — and "disable every category" would not actually empty it.
    id: 'admin',
    labelKey: 'feed.filters.categories.admin',
    icon: 'pi-wrench',
    enabled: true,
    eventTypes: buildEventTypesForCategory('admin'),
  },
];

/** Lookup: event type → category ID */
const EVENT_TYPE_TO_CATEGORY = new Map<string, string>(
  FEED_FILTER_CATEGORIES.flatMap((cat) =>
    cat.eventTypes.map((et) => [et.value, cat.id] as [string, string])
  )
);

/**
 * Compute the list of enabled event types for the API `eventTypes` param.
 * Excludes types whose category is disabled or that are individually disabled.
 */
export function getEnabledEventTypes(
  filters: FeedFilterState
): TimelineEventType[] {
  return ALL_EVENT_TYPES.filter((et) => {
    const categoryId = EVENT_TYPE_TO_CATEGORY.get(et);
    if (categoryId && filters.disabledCategories.includes(categoryId)) {
      return false;
    }
    if (filters.disabledEventTypes.includes(et)) {
      return false;
    }
    return true;
  });
}

/**
 * Count of disabled filter items (for badge display).
 * Includes disabled categories, individual event types, subtypes, and conversations.
 */
export function getDisabledCount(filters: FeedFilterState): number {
  return (
    filters.disabledCategories.length +
    filters.disabledEventTypes.length +
    filters.disabledSubtypes.length +
    (filters.conversationsEnabled ? 0 : 1)
  );
}

/**
 * Returns true when all filters are at default (everything enabled).
 */
export function isDefaultFilterState(filters: FeedFilterState): boolean {
  return (
    filters.conversationsEnabled &&
    filters.disabledCategories.length === 0 &&
    filters.disabledEventTypes.length === 0 &&
    filters.disabledSubtypes.length === 0
  );
}

/**
 * Load filter state from localStorage for a sphere.
 * Handles corruption, missing data, and schema version mismatch.
 */
export function loadFilterState(sphereId: string): FeedFilterState {
  try {
    const raw = localStorage.getItem(`${FILTER_STORAGE_PREFIX}${sphereId}`);
    if (!raw) {
      return { ...DEFAULT_FILTER_STATE };
    }
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      parsed.version !== CURRENT_FILTER_VERSION
    ) {
      // Version mismatch or invalid structure — reset
      localStorage.removeItem(`${FILTER_STORAGE_PREFIX}${sphereId}`);
      return { ...DEFAULT_FILTER_STATE };
    }
    // Validate expected fields exist with correct types
    if (
      typeof parsed.conversationsEnabled !== 'boolean' ||
      !Array.isArray(parsed.disabledCategories) ||
      !Array.isArray(parsed.disabledEventTypes) ||
      !Array.isArray(parsed.disabledSubtypes)
    ) {
      localStorage.removeItem(`${FILTER_STORAGE_PREFIX}${sphereId}`);
      return { ...DEFAULT_FILTER_STATE };
    }
    return parsed as FeedFilterState;
  } catch {
    // Corrupt JSON or localStorage unavailable
    return { ...DEFAULT_FILTER_STATE };
  }
}

/**
 * Save filter state to localStorage for a sphere.
 */
export function saveFilterState(
  sphereId: string,
  state: FeedFilterState
): void {
  try {
    localStorage.setItem(
      `${FILTER_STORAGE_PREFIX}${sphereId}`,
      JSON.stringify(state)
    );
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

/**
 * Returns a fresh default filter state (all filters enabled).
 */
export function resetFilterState(): FeedFilterState {
  return { ...DEFAULT_FILTER_STATE };
}
